import React, { useState, useEffect, useRef, useCallback } from 'react';

const ScientificSpiritDetector = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const containerRef = useRef(null); // Referencia al contenedor principal
  const [isActive, setIsActive] = useState(false);
  const [currentLevel, setCurrentLevel] = useState(0);
  const [entities, setEntities] = useState([]);
  const [capturedImage, setCapturedImage] = useState(null);
  const [showEnergy, setShowEnergy] = useState(true);

  // Configuración de niveles espirituales
  const spiritLevels = [
    { min: 1, max: 3, name: 'Evolucionados', color: '#6366f1', diagnosis: 'Entidades de alta vibración espiritual.' },
    { min: 4, max: 6, name: 'Sabios', color: '#14b8a6', diagnosis: 'Guías espirituales con conocimiento ancestral.' },
    { min: 7, max: 9, name: 'Benevolentes', color: '#10b981', diagnosis: 'Protectores y entidades positivas.' },
    { min: 10, max: 12, name: 'Golpeadores', color: '#f59e0b', diagnosis: 'Entidades con capacidad de interacción física.' },
    { min: 13, max: 15, name: 'Neutros', color: '#64748b', diagnosis: 'Presencias sin intención definida.' },
    { min: 16, max: 18, name: 'Sin Nivel', color: '#8b5cf6', diagnosis: 'Entidades confusas o perdidas.' },
    { min: 19, max: 21, name: 'Livianos', color: '#ec4899', diagnosis: 'Presencias de baja energía.' },
    { min: 22, max: 25, name: 'Impuros', color: '#ef4444', diagnosis: 'Entidades negativas peligrosas.' }
  ];

  // Color de energía
  const getEnergyColor = useCallback((level) => {
    const levelInfo = spiritLevels.find(l => level >= l.min && level <= l.max);
    return levelInfo?.color || '#6366f1';
  }, [spiritLevels]);

  // Iniciar cámara
  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false
      });
      if (videoRef.current && !videoRef.current.srcObject) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Error de cámara:", err);
    }
  }, []);

  // Detener cámara
  const stopCamera = useCallback(() => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
  }, []);

  // Detección de entidades
  const detectEntities = useCallback(() => {
    const newEntities = [];
    const entityCount = Math.floor(Math.random() * 3) + 1;
    
    for (let i = 0; i < entityCount; i++) {
      const level = Math.floor(Math.random() * 25) + 1;
      const type = spiritLevels.find(l => level >= l.min && level <= l.max);
      
      newEntities.push({
        id: Date.now() + i,
        x: Math.random() * 80 + 10,
        y: Math.random() * 60 + 20,
        level,
        type: type?.name || 'Desconocido',
        color: type?.color || '#000000',
        size: Math.floor(Math.random() * 15) + 20
      });
    }
    
    setEntities(newEntities);
  }, [spiritLevels]);

  // Dibujo en canvas (incluye título y hashtag)
  const drawDetections = useCallback(() => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    
    if (!canvas || !video || video.readyState !== 4) {
        animationRef.current = requestAnimationFrame(drawDetections);
        return;
    }

    const ctx = canvas.getContext('2d');
    const { videoWidth, videoHeight } = video;
    canvas.width = videoWidth;
    canvas.height = videoHeight;
    
    // Dibujar el frame del video primero
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Dibujar energía ambiental
    if (showEnergy) {
      ctx.fillStyle = getEnergyColor(currentLevel);
      ctx.globalAlpha = 0.2;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.globalAlpha = 1.0;
    }

    // Dibujar entidades
    entities.forEach(entity => {
      const x = (entity.x / 100) * canvas.width;
      const y = (entity.y / 100) * canvas.height;

      // Aura
      ctx.beginPath();
      ctx.arc(x, y, entity.size * 1.5, 0, Math.PI * 2);
      ctx.fillStyle = `${entity.color}20`;
      ctx.fill();

      // Círculo de entidad
      ctx.beginPath();
      ctx.arc(x, y, entity.size, 0, Math.PI * 2);
      ctx.fillStyle = entity.color;
      ctx.fill();

      // Texto del nivel
      ctx.font = `bold ${entity.size * 0.5}px Arial`;
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(entity.level.toString(), x, y);
    });

    // Dibujar título y hashtag en el canvas
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 20px Arial';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText('Detector de Espíritus Dr. David García', 20, 20);

    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'bottom';
    ctx.fillText('#aurapower', canvas.width - 20, canvas.height - 20);


    animationRef.current = requestAnimationFrame(drawDetections);
  }, [entities, currentLevel, showEnergy, getEnergyColor]);

  // Obtener diagnóstico completo
  const getFullDiagnosis = useCallback(() => {
    const entity = spiritLevels.find(l => 
      currentLevel >= l.min && currentLevel <= l.max
    ) || spiritLevels[0];

    let recommendationText = "";
    if (currentLevel > 20) {
        recommendationText = "¡Alerta máxima! Use loción Urania y evacue el área";
    } else if (currentLevel > 10) {
        recommendationText = "Precaución: Realice limpieza energética con loción Urania";
    } else {
        recommendationText = "Área segura, no se requiere acción";
    }

    return {
      name: entity.name,
      diagnosis: entity.diagnosis,
      recommendation: recommendationText
    };
  }, [currentLevel, spiritLevels]);

  // Capturar foto y guardar (ahora captura el canvas completo)
  const capturePhoto = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    try {
      const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
      setCapturedImage(dataUrl);
      
      // Intentar guardar en galería (requiere permisos y soporte del navegador/OS)
      const link = document.createElement('a');
      link.download = `paranormal_evidence_${Date.now()}.jpg`;
      link.href = dataUrl;
      link.click();

    } catch (error) {
      console.error("Error al capturar o guardar:", error);
    }
  }, []);

  // Compartir evidencia
  const shareEvidence = useCallback(() => {
    if (!capturedImage) return;

    const currentDiagnosis = getFullDiagnosis();

    const shareText = `👻 Detector de Espíritus Dr. David García 👻\n` +
                     `Nivel EMF detectado: ${currentLevel}\n` +
                     `Tipo de entidad: ${currentDiagnosis.name}\n` +
                     `Diagnóstico: ${currentDiagnosis.diagnosis}\n` +
                     `Recomendación del Dr. García: ${currentDiagnosis.recommendation}`;

    try {
      // Usar Web Share API si está disponible
      if (navigator.share) {
        fetch(capturedImage)
          .then(res => res.blob())
          .then(blob => {
            const file = new File([blob], "paranormal_evidence.jpg", { type: "image/jpeg" });
            navigator.share({
              title: 'Evidencia Paranormal',
              text: shareText,
              files: [file]
            }).catch(error => console.error('Error sharing:', error));
          })
          .catch(error => console.error('Error fetching image for share:', error));
      } else {
        // Fallback para WhatsApp (solo texto)
        const whatsappUrl = `whatsapp://send?text=${encodeURIComponent(shareText)}`;
        window.open(whatsappUrl, '_blank');
      }
    } catch (error) {
      console.error("Error al intentar compartir:", error);
    }
  }, [capturedImage, currentLevel, getFullDiagnosis]);

  // Efecto principal para iniciar/detener detección y cámara
  useEffect(() => {
    if (isActive) {
      startCamera();
      const interval = setInterval(() => {
        const randomLevel = Math.floor(Math.random() * 25) + 1;
        setCurrentLevel(randomLevel);
        detectEntities();
      }, 2000);

      animationRef.current = requestAnimationFrame(drawDetections);

      return () => {
        clearInterval(interval);
        cancelAnimationFrame(animationRef.current);
        stopCamera();
      };
    } else {
      stopCamera();
      cancelAnimationFrame(animationRef.current);
      setEntities([]); // Limpiar entidades al detener
      setCapturedImage(null); // Limpiar captura al detener
    }
  }, [isActive, startCamera, stopCamera, detectEntities, drawDetections]);

  // Limpieza al desmontar el componente
  useEffect(() => {
    return () => {
      stopCamera();
      cancelAnimationFrame(animationRef.current);
    };
  }, [stopCamera]);

  return (
    <div ref={containerRef} className="relative min-h-screen bg-gray-900 text-white">
      {/* Título en la parte superior */}
      <div className="absolute top-0 left-0 right-0 bg-black bg-opacity-70 p-4 text-center z-10">
        <h1 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-500">
          Detector de Espíritus Dr. David García
        </h1>
      </div>

      <div className="relative h-screen w-full">
        {/* Video de la cámara */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="absolute inset-0 w-full h-full object-cover"
        />
        
        {/* Canvas para dibujar detecciones */}
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full object-cover"
        />
        
        {/* Controles en la parte inferior */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-6">
          {/* Indicador de nivel */}
          <div className="flex justify-between items-center mb-4">
            <div className="bg-black bg-opacity-70 px-4 py-2 rounded-full">
              <span 
                className="font-mono text-xl font-bold" 
                style={{ color: getEnergyColor(currentLevel) }}
              >
                {currentLevel} EMF
              </span>
            </div>
            
            {/* Botón para mostrar/ocultar energía */}
            <button
              onClick={() => setShowEnergy(!showEnergy)}
              className={`px-4 py-2 rounded-full ${showEnergy ? 'bg-blue-600' : 'bg-gray-700'}`}
            >
              {showEnergy ? 'Ocultar Energía' : 'Mostrar Energía'}
            </button>
          </div>
          
          {/* Botones principales */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            {/* Botón de inicio/detención */}
            <button
              onClick={() => setIsActive(!isActive)}
              className={`py-3 rounded-full font-bold ${isActive ? 'bg-red-600' : 'bg-green-600'}`}
            >
              {isActive ? 'Detener' : 'Iniciar'}
            </button>
            
            {/* Botón de captura */}
            <button
              onClick={capturePhoto}
              disabled={!isActive}
              className={`py-3 rounded-full font-bold bg-purple-600 ${!isActive && 'opacity-50'}`}
            >
              Capturar
            </button>
            
            {/* Botón de compartir */}
            <button
              onClick={shareEvidence}
              disabled={!capturedImage}
              className={`py-3 rounded-full font-bold bg-blue-600 ${!capturedImage && 'opacity-50'}`}
            >
              Compartir Evidencia
            </button>
          </div>
          
          {/* Vista previa de la captura */}
          {capturedImage && (
            <div className="bg-black bg-opacity-70 p-4 rounded-xl mb-4">
              <div className="flex items-center justify-between">
                <span className="text-sm">Foto capturada</span>
                <div className="w-16 h-16 border-2 border-white rounded-md overflow-hidden">
                  <img 
                    src={capturedImage} 
                    alt="Captura" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          )}
          
          {/* Información de diagnóstico */}
          <div className="bg-black bg-opacity-70 p-4 rounded-xl">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-bold mb-1">Diagnóstico Científico</h3>
                <p className="text-sm"><strong>Tipo:</strong> {getFullDiagnosis().name}</p>
                <p className="text-sm"><strong>Análisis:</strong> {getFullDiagnosis().diagnosis}</p>
                <p className="text-sm mt-2"><strong>Dr. García recomienda:</strong> {getFullDiagnosis().recommendation}</p>
              </div>
              <div className="text-xs bg-gray-700 px-2 py-1 rounded">
                Nivel {currentLevel}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScientificSpiritDetector;